'use client';
import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Building2, Landmark, TreePine, ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';

const LRA_PROPERTY_TYPES = [
  // Developed Land
  { id: 'dev-comm', category: 'Developed', label: 'Commercial', rate: 1.5, rateDisplay: '1.5%', color: '#d4a017', collections: 18.2, properties: 3100, compliance: 74 },
  { id: 'dev-ind', category: 'Developed', label: 'Industrial', rate: 1.5, rateDisplay: '1.5%', color: '#e07b17', collections: 10.2, properties: 1720, compliance: 69 },
  { id: 'dev-res', category: 'Developed', label: 'Residential', rate: 0.25, rateDisplay: '0.25%', color: '#f0c040', collections: 18.7, properties: 38200, compliance: 61 },
  { id: 'dev-farm-urban', category: 'Developed', label: 'Farm Land (Urban)', rate: 0.33, rateDisplay: '0.33%', color: '#fbbf24', collections: 4.2, properties: 3100, compliance: 55 },
  { id: 'dev-farm-outside', category: 'Developed', label: 'Farm Land (Outside Urban)', rate: 0.25, rateDisplay: '0.25%', color: '#f59e0b', collections: 3.1, properties: 5400, compliance: 48 },
  { id: 'dev-pub-comm', category: 'Developed', label: 'Public Land (Commercial)', rate: 1.0, rateDisplay: '1.0%', color: '#d97706', collections: 9.6, properties: 1240, compliance: 84 },
  { id: 'dev-pub-res', category: 'Developed', label: 'Public Land (Residential)', rate: 0.14, rateDisplay: '0.14%', color: '#b45309', collections: 2.8, properties: 6700, compliance: 79 },
  { id: 'dev-mixed', category: 'Developed', label: 'Mixed Property', rate: 1.5, rateDisplay: '1.5%', color: '#a78bfa', collections: 6.5, properties: 2180, compliance: 67 },
  // Undeveloped Land
  { id: 'und-city', category: 'Undeveloped', label: 'City / Town Lots', rate: 3.5, rateDisplay: '3.5%', color: '#60a5fa', collections: 12.3, properties: 9800, compliance: 43 },
  { id: 'und-farm', category: 'Undeveloped', label: 'Farmland', rate: 4.0, rateDisplay: '4.0%', color: '#3b82f6', collections: 7.8, properties: 14200, compliance: 38 },
  { id: 'und-vacant', category: 'Undeveloped', label: 'Vacant Land (1+ Acre)', rate: 4.5, rateDisplay: '4.5%', color: '#2563eb', collections: 5.4, properties: 6300, compliance: 35 },
  { id: 'und-outside', category: 'Undeveloped', label: 'Outside City/Town', rate: 0, rateDisplay: 'LRD $5/acre', color: '#1d4ed8', collections: 1.9, properties: 22100, compliance: 29 },
];

const CATEGORY_META = {
  Developed: { icon: Building2, color: '#d4a017', bg: 'rgba(212,160,23,0.12)', label: 'Developed Land' },
  Undeveloped: { icon: TreePine, color: '#60a5fa', bg: 'rgba(96,165,250,0.1)', label: 'Undeveloped Land' },
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs shadow-xl"
      style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' }}
    >
      <p className="font-semibold mb-1">{label}</p>
      <p style={{ color: '#d4a017' }}>Collections: <span className="font-bold">${payload[0]?.value}M</span></p>
    </div>
  );
}

export default function PropertyTypeBreakdown() {
  const [expanded, setExpanded] = useState(true);
  const [activeCategory, setActiveCategory] = useState<'All' | 'Developed' | 'Undeveloped'>('All');

  const filtered = activeCategory === 'All' ? LRA_PROPERTY_TYPES : LRA_PROPERTY_TYPES.filter(p => p.category === activeCategory);
  const totalCollections = LRA_PROPERTY_TYPES.reduce((s, p) => s + p.collections, 0);
  const totalProperties = LRA_PROPERTY_TYPES.reduce((s, p) => s + p.properties, 0);

  const chartData = filtered.map(p => ({ name: p.label.length > 20 ? p.label.slice(0, 18) + '…' : p.label, value: p.collections, color: p.color }));

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 border-b cursor-pointer"
        style={{ borderColor: 'var(--dark-border)' }}
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(212,160,23,0.15)' }}>
            <Landmark size={16} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>LRA Real Property Tax — All Property Types</h3>
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
              {LRA_PROPERTY_TYPES.length} categories · ${totalCollections.toFixed(1)}M YTD · {totalProperties.toLocaleString()} registered parcels
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs px-2 py-1 rounded-full font-semibold" style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}>
            Official LRA Rates
          </span>
          {expanded ? <ChevronUp size={16} style={{ color: 'var(--dark-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--dark-muted)' }} />}
        </div>
      </div>

      {expanded && (
        <div className="p-5">
          {/* Category filter */}
          <div className="flex items-center gap-2 mb-5">
            {(['All', 'Developed', 'Undeveloped'] as const).map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150"
                style={{
                  backgroundColor: activeCategory === cat ? 'var(--accent)' : 'rgba(255,255,255,0.06)',
                  color: activeCategory === cat ? 'var(--accent-foreground)' : 'var(--dark-muted)',
                  border: `1px solid ${activeCategory === cat ? 'var(--accent)' : 'var(--dark-border)'}`,
                }}
              >
                {cat === 'All' ? 'All Types' : CATEGORY_META[cat].label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-1.5 text-xs" style={{ color: 'var(--dark-muted)' }}>
              <TrendingUp size={12} style={{ color: '#4ade80' }} />
              <span>YTD Collections</span>
            </div>
          </div>

          {/* Chart */}
          <div className="h-44 mb-5">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: 'var(--dark-muted)' }} axisLine={false} tickLine={false} interval={0} />
                <YAxis tick={{ fontSize: 9, fill: 'var(--dark-muted)' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}M`} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Property type cards grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-3">
            {filtered.map(pt => {
              const meta = CATEGORY_META[pt.category as keyof typeof CATEGORY_META];
              const share = ((pt.collections / totalCollections) * 100).toFixed(1);
              return (
                <div
                  key={pt.id}
                  className="rounded-lg p-3 flex flex-col gap-2"
                  style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: `1px solid var(--dark-border)` }}
                >
                  <div className="flex items-start justify-between gap-1">
                    <div>
                      <span
                        className="text-xs font-semibold px-1.5 py-0.5 rounded mb-1 inline-block"
                        style={{ backgroundColor: meta.bg, color: meta.color, fontSize: '9px' }}
                      >
                        {pt.category}
                      </span>
                      <p className="text-xs font-semibold leading-tight" style={{ color: 'var(--dark-text)' }}>{pt.label}</p>
                    </div>
                    <span
                      className="text-xs font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)', fontSize: '10px' }}
                    >
                      {pt.rateDisplay}
                    </span>
                  </div>
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-base font-bold tabular-nums" style={{ color: pt.color }}>${pt.collections}M</p>
                      <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{pt.properties.toLocaleString()} parcels</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-semibold" style={{ color: 'var(--dark-muted)' }}>{share}% share</p>
                      <p className="text-xs" style={{ color: pt.compliance >= 60 ? '#4ade80' : pt.compliance >= 45 ? '#fbbf24' : '#f87171' }}>
                        {pt.compliance}% compliant
                      </p>
                    </div>
                  </div>
                  {/* Compliance bar */}
                  <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${pt.compliance}%`,
                        backgroundColor: pt.compliance >= 60 ? '#4ade80' : pt.compliance >= 45 ? '#fbbf24' : '#f87171',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer note */}
          <div
            className="mt-4 px-3 py-2 rounded-lg text-xs flex items-start gap-2"
            style={{ backgroundColor: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.15)', color: 'var(--dark-muted)' }}
          >
            <Landmark size={12} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} />
            <span>
              Rates sourced from <span style={{ color: 'var(--accent)' }}>revenue.lra.gov.lr</span> — Real Property Tax Act, Section 51. 
              Annual tax = Declared Market Value × Rate. Due date: July 1st. TIN required for all filings.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
